import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import {
  useAccommodationClient,
  useAccommodationGuests,
  useGuestDetails,
  useUpdateAccommodationClient,
} from '@hooks/accommodation';
import { selectSelectedGuest, setSelectedGuest } from '@store/accommodation';
import { selectEffectiveOutletId } from '@store/outlets';
import { Receipt, Search, Users } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { AccommodationGuest } from '../../../../types/accommodation';
import { AccommodationGuestsTable } from './AccommodationGuestsTable';
import { GuestEditorModal } from './GuestEditorModal';
import { GuestPreviewPanel } from './GuestPreviewPanel';

// ─── component ────────────────────────────────────────────────────────────────

export const AccommodationGuestsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const outletId = useAppSelector(selectEffectiveOutletId);
  const selectedGuest = useAppSelector(selectSelectedGuest);

  const {
    data: guests = [],
    isLoading,
    error: guestsQueryError,
  } = useAccommodationGuests(outletId);
  const error = guestsQueryError ? (guestsQueryError as Error).message : null;

  const {
    data: guestDetails = null,
    isLoading: isGuestDetailsLoading,
    error: guestDetailsQueryError,
  } = useGuestDetails(selectedGuest?.id, outletId);
  const guestDetailsError = guestDetailsQueryError
    ? (guestDetailsQueryError as Error).message
    : null;

  const [search, setSearch] = useState('');
  const [isEditingClient, setIsEditingClient] = useState(false);

  const { data: editingClient } = useAccommodationClient(
    isEditingClient ? selectedGuest?.id : undefined,
  );
  const updateClient = useUpdateAccommodationClient();

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return guests;
    return guests.filter(
      (g) =>
        `${g.first_name} ${g.last_name}`.toLowerCase().includes(q) ||
        (g.email ?? '').toLowerCase().includes(q) ||
        (g.contact ?? '').includes(q) ||
        (g.country_name ?? '').toLowerCase().includes(q),
    );
  }, [guests, search]);

  // KPI summary
  const totalGuests = guests.length;
  const currentlyStaying = guests.filter(
    (g) => g.latest_booking_status === 'checked_in',
  ).length;
  const withOutstandingBalance = guests.filter(
    (g) => g.outstanding_credit_balance > 0,
  ).length;
  const totalOutstanding = guests.reduce(
    (s, g) => s + g.outstanding_credit_balance,
    0,
  );

  const handleGuestClick = (guest: AccommodationGuest) => {
    dispatch(setSelectedGuest(guest));
  };

  const handleClosePreview = () => {
    dispatch(setSelectedGuest(null));
  };

  const handleRetryPreview = () => {
    // No-op: TanStack Query refetches when enabled/stale; selecting
    // the same guest again will trigger a refetch via invalidation.
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Guests</h2>
          <p className="text-xs text-gray-400">
            All guests who have made at least one accommodation booking
          </p>
        </div>
      </div>

      {/* KPI chips */}
      <div className="grid gap-3 sm:grid-cols-4">
        <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-pos-primary/10">
            <Users size={15} className="text-pos-primary" />
          </div>
          <div>
            <p className="text-xs text-gray-400">Total Guests</p>
            <p className="text-base font-bold text-gray-900">{totalGuests}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-teal-50">
            <Users size={15} className="text-teal-600" />
          </div>
          <div>
            <p className="text-xs text-gray-400">Currently Staying</p>
            <p className="text-base font-bold text-gray-900">
              {currentlyStaying}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-amber-50">
            <Receipt size={15} className="text-amber-600" />
          </div>
          <div>
            <p className="text-xs text-gray-400">With Outstanding Bills</p>
            <p className="text-base font-bold text-gray-900">
              {withOutstandingBalance}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-red-50">
            <Receipt size={15} className="text-red-500" />
          </div>
          <div>
            <p className="text-xs text-gray-400">Total Outstanding</p>
            <p className="text-base font-bold text-gray-900">
              {totalOutstanding.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search
          size={14}
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          placeholder="Search by name, email, phone or country…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-4 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-pos-primary focus:ring-1 focus:ring-pos-primary"
        />
      </div>

      <AccommodationGuestsTable
        guests={filtered}
        isLoading={isLoading}
        error={error}
        search={search}
        onGuestClick={handleGuestClick}
      />

      <GuestPreviewPanel
        isOpen={selectedGuest !== null}
        guest={selectedGuest}
        details={guestDetails}
        isLoading={isGuestDetailsLoading}
        error={guestDetailsError}
        onClose={handleClosePreview}
        onRetry={handleRetryPreview}
        onEdit={() => setIsEditingClient(true)}
      />

      {isEditingClient && editingClient && (
        <GuestEditorModal
          client={editingClient}
          isSaving={updateClient.isPending}
          onClose={() => setIsEditingClient(false)}
          onSubmit={(form) => {
            updateClient.mutate(
              { id: editingClient.id, updates: form },
              { onSuccess: () => setIsEditingClient(false) },
            );
          }}
        />
      )}
    </div>
  );
};
